import { VStack, Box, HStack, For, chakra, Spinner, Center } from '@chakra-ui/react';
import {
  SortableGrid,
  SortableGridItem,
  CallMainPanelFooter,
  CallerInfo,
  CallPinIcon,
} from '@halo/ui-chat-react';
import React from 'react';
import { MediaSDKMediaType, SubscribeStatus, SubscribeType } from '@halo/chat-sdk';
import { useLatest, useUpdateEffect } from 'ahooks';
import { useTranslation } from 'react-i18next';

export interface CallMainPanelProps {
  rightRef: any;
  fixedMembers?: any;
  pinChildren?: React.ReactNode;
  rightChildren?: React.ReactNode;
}
export function CallMainPanel({
  rightRef,
  fixedMembers,
  pinChildren,
  rightChildren,
}: CallMainPanelProps) {
  return (
    <HStack flex="1" w="full" gap="2.5" overflowY="hidden">
      {pinChildren && (
        <Box flex={fixedMembers.length ? '1' : '0'} w="full" h="full">
          {pinChildren}
        </Box>
      )}

      <Box
        className="scrollHover"
        flex="1"
        h="full"
        ref={rightRef}
        overflowY="auto"
        id="mediaModalRight"
      >
        {rightChildren}
      </Box>
    </HStack>
  );
}

export interface CallSortablePanelItem {
  id: string;
  name: string;
  avatar: string;
  bg?: string;
  isSpeaking?: boolean;
  screenSharing: boolean;
  user_id: string;
  isSelf: boolean;
}

export interface CallSortablePanelProps {
  handleDragEnd: (items: Record<string, any>[]) => void;
  handleDrawSpectrum: (...args: any) => void;
  noFixedMembers: Array<CallSortablePanelItem>;
  gridTemplateColumns: string;
  gridAutoRowsHeight: number;
  fullScreen: boolean;
  onFixed: (member: CallSortablePanelItem) => void;
  ondbClick?: (member: CallSortablePanelItem) => void;
  onSubscribe: (member: any, status: SubscribeStatus) => void;
  subscribeCollection: (members: Record<string, any>[], type: SubscribeType) => Promise<any>;
}

export function CallSortablePanel({
  handleDrawSpectrum,
  handleDragEnd,
  noFixedMembers,
  gridTemplateColumns,
  gridAutoRowsHeight,
  fullScreen,
  onFixed,
  ondbClick,
  onSubscribe,
  subscribeCollection,
}: CallSortablePanelProps) {
  const { t } = useTranslation();
  const [audioInfo, setAudioInfo] = useState({ hasAudioStream: false, hasVolume: false });
  return (
    <SortableGrid onDragEnd={handleDragEnd} items={noFixedMembers}>
      <Box
        display="grid"
        gridTemplateColumns={gridTemplateColumns}
        gridAutoRows={gridAutoRowsHeight + 'px'}
        gap="2.5"
      >
        <For each={noFixedMembers}>
          {(member, index) => {
            return (
              <SortableGridItem
                id={member.id}
                key={member.id}
                disabled={!fullScreen || noFixedMembers.length === 1}
                hidden={!fullScreen && index !== 0}
              >
                {(isDragging) => {
                  return (
                    <CallCardPanel
                      avatar={member?.avatar}
                      avatarSize={2000}
                      isDragging={isDragging}
                      bg={member.bg}
                      member={member}
                      rearChildren={
                        <CallMainPanelFooter
                          disabled={false}
                          member={member}
                          handleDrawSpectrum={handleDrawSpectrum}
                          onAudioInfo={(info) => setAudioInfo(info)}
                          onFixed={() => onFixed(member)}
                        />
                      }
                      ondbClick={() => ondbClick?.(member)}
                      subscribeCollection={subscribeCollection}
                      updateSubscribeStatus={(status) => onSubscribe(member, status)}
                      gridAutoRowsHeight={gridAutoRowsHeight}
                      index={index}
                    >
                      <CallerInfo
                        showBg={audioInfo.hasAudioStream}
                        hasVolume={audioInfo.hasVolume}
                        avatar={member?.avatar}
                        name={member.isSelf ? t('calls.me') : member?.name}
                        isConnect={true}
                        size="3xl"
                      />
                    </CallCardPanel>
                  );
                }}
              </SortableGridItem>
            );
          }}
        </For>
      </Box>
    </SortableGrid>
  );
}

export interface CallCardPanelrops {
  sideChildren?: React.ReactNode;
  frontChildren?: React.ReactNode;
  children: React.ReactNode;
  rearChildren: React.ReactNode;
  bg?: string;
  isDragging?: boolean;
  member?: Record<string, any>;
  ringing?: boolean;
  onFixed?: (member: Record<string, any>) => void;
  ondbClick?: (member: Record<string, any>) => void;
  avatar?: string;
  avatarSize?: number;
  index?: number;
  gridAutoRowsHeight?: number;
  updateSubscribeStatus?: (status: SubscribeStatus) => void;
  subscribeCollection?: (members: Record<string, any>[], type: SubscribeType) => Promise<any>;
}

export function CallCardPanel({
  sideChildren,
  frontChildren,
  children,
  bg,
  avatar,
  avatarSize = 800,
  rearChildren,
  member,
  // ringing = true,
  index,
  isDragging,
  onFixed,
  ondbClick,
  subscribeCollection,
  updateSubscribeStatus,
}: CallCardPanelrops) {
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const latest = useLatest({ subscribeCollection, updateSubscribeStatus });

  // TODO amo
  const stream = useMemo<MediaStream | undefined>(() => {
    if (member?.screenSharing) {
      return member?.streams?.screenSharing_video?.enabled
        ? member?.streams?.screenSharing_video?.stream
        : undefined;
    } else {
      return member?.streams?.microphoneCamera_video?.enabled
        ? member?.streams?.microphoneCamera_video?.stream
        : undefined;
    }
  }, [
    member?.streams?.microphoneCamera_video,
    member?.streams?.screenSharing_video,
    member?.screenSharing,
  ]);

  const track = useMemo(
    () =>
      member?.tracks?.find((track: any) =>
        member?.screenSharing
          ? track.mediaType === MediaSDKMediaType.SCREENSHARING_VIDEO
          : track.mediaType === MediaSDKMediaType.MICROPHONECAMERA_VIDEO
      ),
    [member?.tracks, member?.screenSharing]
  );

  useUpdateEffect(() => {
    const { updateSubscribeStatus } = latest.current;
    if (track?.trackName) {
      updateSubscribeStatus?.(SubscribeStatus.DEFAULT);
    }
  }, [track?.trackName, latest]);

  useEffect(() => {
    const dom = videoRef.current;
    if (dom) {
      if (stream) {
        dom.srcObject = stream;
        dom.load();
        dom.onloadeddata = () => {
          if (SubscribeStatus.SUBSCRIBE_SUCCESS !== member?.subscribeStatus) {
            updateSubscribeStatus?.(SubscribeStatus.SUBSCRIBE_SUCCESS);
          }
        };
        dom.play();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stream,
    member?.callActionMap?.microphoneCamera_video,
    member?.callActionMap?.screenSharing_video,
  ]);

  const handleRetrySubscribe = useCallback(async () => {
    const { subscribeCollection, updateSubscribeStatus } = latest.current;
    if (
      (member?.streams?.screenSharing_video?.enabled ||
        member?.streams?.microphoneCamera_video?.enabled) &&
      subscribeCollection &&
      updateSubscribeStatus
    ) {
      updateSubscribeStatus(SubscribeStatus.SUBSCRIBEING);
      const subscribeInfo = await subscribeCollection([member], SubscribeType.SUBSCRIBE)?.catch(
        () => {
          updateSubscribeStatus(SubscribeStatus.SUBSCRIBE_FAIL);
        }
      );
      if (subscribeInfo === true) {
        updateSubscribeStatus(SubscribeStatus.SUBSCRIBE_SUCCESS);
      } else if (subscribeInfo?.detail?.fail?.includes(track?.trackName)) {
        updateSubscribeStatus(SubscribeStatus.SUBSCRIBE_FAIL);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    latest,
    member?.streams?.microphoneCamera_video,
    member?.streams?.screenSharing_video,
    track,
  ]);

  useEffect(() => {
    const { subscribeCollection, updateSubscribeStatus } = latest.current;
    if (
      member?.isSelf ||
      isDragging ||
      (!member?.callActionMap?.microphoneCamera_video && !member?.screenSharing) ||
      !subscribeCollection ||
      !updateSubscribeStatus
    ) {
      if (
        !member?.callActionMap?.microphoneCamera_video &&
        !member?.screenSharing &&
        member?.subscribeStatus === SubscribeStatus.SUBSCRIBEING
      ) {
        updateSubscribeStatus?.(SubscribeStatus.DEFAULT);
      }
      return;
    }

    const observer_ = new IntersectionObserver(
      async ([entry]) => {
        if (track) {
          if (entry.isIntersecting) {
            if (member?.subscribeStatus === SubscribeStatus.DEFAULT) {
              handleRetrySubscribe();
            }
          } else {
            if (index === 0) {
              return;
            }

            if (member?.subscribeStatus === SubscribeStatus.SUBSCRIBE_SUCCESS) {
              subscribeCollection([member], SubscribeType.STOPSUBSCRIBE)
                ?.catch(() => {})
                .finally(() => {
                  updateSubscribeStatus(SubscribeStatus.DEFAULT);
                });
            }
          }
        }
      },
      {
        threshold: 0.1,
        root: document.querySelector('.mediaModalRight'),
      }
    );
    if (cardRef.current) {
      observer_.observe(cardRef.current);
    }
    return () => {
      observer_.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    latest,
    track,
    member?.subscribeStatus,
    isDragging,
    member?.callActionMap?.microphoneCamera_video,
    index,
  ]);

  return (
    <VStack
      ref={cardRef}
      pos="relative"
      flex="1"
      width="full"
      height="full"
      color="white"
      // bg={
      //   ringing
      //     ? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7))${bg ? `, ${bg}` : ''}`
      //     : 'none'
      // }
      bg={avatar ? 'lightgray' : `linear-gradient(rgba(0,0,0,0.7)), ${bg}`}
      _before={
        avatar
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              bgImage: `url(${avatar})`,
              bgSize: `${avatarSize}px ${avatarSize}px`,
              bgPos: 'center',
              bgRepeat: 'no-repeat',
              filter: `blur(${avatarSize / 10}px)`,
              zIndex: -1,
            }
          : undefined
      }
      backdropFilter="blur(60px)"
      rounded="xl"
      justifyContent="center"
      alignItems="center"
      overflow="hidden"
      border={member?.isSpeaking ? '2px solid #00BC00' : '2px solid #00BC0'}
      onDoubleClick={ondbClick}
      zIndex={1}
    >
      {/* fixed */}
      {member?.fixed && (
        <Box
          position="absolute"
          right="12px"
          top="12px"
          p="2"
          bg="blackAlpha.20"
          rounded="full"
          backdropFilter="blur(4px)"
          zIndex={10}
          cursor="pointer"
          onClick={() => onFixed?.(member)}
        >
          <CallPinIcon />
        </Box>
      )}
      {sideChildren && (
        <Box pos="absolute" bottom="67px" right="12px" width="222px" height="158px">
          {sideChildren}
        </Box>
      )}

      {frontChildren}
      <VStack flex="1" alignItems="center" justify="center" pos="relative" w="full" h="full">
        {/* TODO amo: Do not if stream */}
        {!stream ||
        (member?.screenSharing
          ? !member?.callActionMap?.screenSharing_video
          : !member?.callActionMap?.microphoneCamera_video) ? (
          children
        ) : (
          <Box w="full" h="full">
            <chakra.video
              w="full"
              h="full"
              objectFit="contain"
              playsInline
              autoPlay
              muted
              loop
              ref={videoRef}
            ></chakra.video>
          </Box>
        )}
        {/* bottom */}
        <Box pos="absolute" bottom="0" w="full" zIndex="1">
          {React.cloneElement(rearChildren as any, { cardRef })}
        </Box>
      </VStack>
      {member?.subscribeStatus === SubscribeStatus.SUBSCRIBEING && (
        <Box pos="absolute" inset="0" bg="rgba(0, 0, 0, 0.5)">
          <Center h="full">
            <Spinner />
          </Center>
        </Box>
      )}
    </VStack>
  );
}

export default CallCardPanel;
